/*
 * Class for Global Log object
 */

import { EnumObjects, EventFlags } from '@microsoft/oteljs'

import { assert, debug } from './console-log'
import { IConfiguration } from './helpers/environment'
import {
  CustomLogData,
  EventData,
  IBeginEventData,
  IEventPayloadData,
  ILogEntry,
  ILogSink,
  InstrumentOptions,
  ISinkContext,
  LogLevel,
  MessageData,
  MessageOptions,
} from './interfaces'
import { getMessageData, getMetaData, serializeObject } from './log-helpers'

export type TagId = number & { readonly __tag: unique symbol }

const mergeWithDefaultEventFlags = (
  eventFlags?: Partial<EventFlags>,
  defaultEventFlags: EventFlags = {
    dataCategories: EnumObjects.DataCategories.ProductServicePerformance,
    diagnosticLevel: EnumObjects.DiagnosticLevel.RequiredServiceData,
  }
) => ({
  ...defaultEventFlags,
  ...eventFlags,
})

// logger class that creates message/event objects and finally writes to all the configured sinks
export class Logger {
  // local assertions
  public assert = assert

  // local debug assertions
  public debug = debug

  // logSinks - sinks to write to
  // logLevel - min threshold above which messages are logged
  constructor(
    private logSinks: ILogSink[],
    private configuration?: IConfiguration,
    private logLevel: LogLevel = LogLevel.Info
  ) {}

  // clears sinks
  public clearSinks = (): void => {
    this.logSinks = []
  }

  // sets min loglevel threshold to prevent any unnecessary logging/noise
  public setLogLevel = (logLevel: LogLevel) => (this.logLevel = logLevel)

  public getLogLevel = () => this.logLevel

  public getConfiguration = () =>
    this.configuration as Readonly<IConfiguration> | undefined

  public setConfiguration = (config: IConfiguration) =>
    (this.configuration = config)

  public updateCustomData = (customMetadata?: CustomLogData) => {
    if (!this.configuration) {
      return
    }

    this.configuration.CustomMetadata = {
      ...this.configuration.CustomMetadata,
      ...customMetadata,
    }
  }

  // adds a new sink to list
  public addSink = (logSink: ILogSink) => {
    if (!logSink) {
      assert(logSink, 'Invalid logSink!')

      return
    }

    const callbackFunc = (sinkItem: ILogSink) => {
      return sinkItem.name() === logSink.name()
    }

    // check if sink already exists
    //  .find is not available in IE use filter instead
    // const existingSinks = this.logSinks.find(sinkItem => sinkItem.constructor.name === logSink.constructor.name);
    const existingSinks: ILogSink[] = this.logSinks.filter(callbackFunc)
    debug(
      !existingSinks || existingSinks.length === 0,
      'Sink already exists',
      logSink,
      this.logSinks
    )
    if (existingSinks && existingSinks.length > 0) {
      return
    }

    this.logSinks.push(logSink)
  }

  // removes sinks from list
  public removeSinks = (name: string) => {
    const matchingSinks = this.logSinks.filter(
      (sinkItem: ILogSink) => sinkItem.name() === name
    )
    matchingSinks.forEach((sinkItem: ILogSink) => {
      const sIndex = this.logSinks.indexOf(sinkItem)
      this.logSinks.splice(sIndex, 1)
    })
  }

  // helper method to get automated tagger to generate a tag
  public reserve = (tagId: number) => tagId as TagId

  // trace level log
  public trace = (
    tagId: number,
    message: MessageOptions,
    ...data: unknown[]
  ) => {
    const logEntry = this.createMessageLogEntry(
      getMessageData(LogLevel.Trace, tagId, message, data)
    )
    this.writeEntry(logEntry)
  }

  // info level log
  public info = (
    tagId: number,
    message: MessageOptions,
    ...data: unknown[]
  ) => {
    const logEntry = this.createMessageLogEntry(
      getMessageData(LogLevel.Info, tagId, message, data)
    )
    this.writeEntry(logEntry)
  }

  // warn level log
  public warn = (
    tagId: number,
    message: MessageOptions,
    ...data: unknown[]
  ) => {
    const logEntry = this.createMessageLogEntry(
      getMessageData(LogLevel.Warning, tagId, message, data)
    )
    this.writeEntry(logEntry)
  }

  // error level log
  public error = (
    tagId: number,
    message: MessageOptions,
    ...data: unknown[]
  ) => {
    const logEntry = this.createMessageLogEntry(
      getMessageData(LogLevel.Error, tagId, message, data)
    )
    this.writeEntry(logEntry)
  }

  // Local log
  public local = (message: MessageOptions, ...data: unknown[]) => {
    const logEntry = this.createMessageLogEntry(
      getMessageData(LogLevel.Info, 0x10ca1, message, data)
    )
    this.writeEntry(logEntry, true)
  }

  // Local log
  public localWarn = (message: MessageOptions, ...data: unknown[]) => {
    const logEntry = this.createMessageLogEntry(
      getMessageData(LogLevel.Warning, 0x10ca1, message, data)
    )
    this.writeEntry(logEntry, true)
  }

  // adds telemetry/instruments data around a given function
  public instrument = async <T>(
    eventTagId: number,
    eventActionType: string,
    func: (eventData: IEventPayloadData) => Promise<T> | T,
    {
      logError = true,
      onEndEvent,
      rethrow = true,
      ...eventData
    }: InstrumentOptions = {}
  ) => {
    const beginEventData = this.beginEvent(
      eventTagId,
      eventActionType,
      eventData
    )

    if (!eventData.payloadData) {
      eventData.payloadData = {}
    }
    const { payloadData } = eventData

    const endEvent = () => {
      this.endEvent(beginEventData, payloadData)
      if (onEndEvent) {
        onEndEvent(payloadData.resultType)
      }
    }

    let retValue: T | null = null
    try {
      retValue = await func(payloadData)
      payloadData.resultType = payloadData.resultType || 'None'
      endEvent()
    } catch (_error) {
      const error = _error as { code: number }

      if (logError) {
        // tslint:disable-next-line:no-this-assignment
        const log = this // to make tagger happy
        log.error(
          0x23557080 /* tag_9vxca */,
          'Instrumentation error handler',
          eventTagId,
          eventActionType,
          error
        )
      }

      payloadData.resultType = 'Failure'
      if (!payloadData.resultInfo && logError) {
        payloadData.resultInfo = serializeObject(error)
      }

      if (!payloadData.responseCode && error.code) {
        payloadData.responseCode = error.code
      }

      endEvent()
      if (rethrow) {
        throw error
      }
    }

    return retValue
  }

  // telemetry/instrumentation data event
  public event = (
    eventTagId: number,
    eventActionType: string,
    eventData: EventData = {}
  ) => {
    const beginEventData = this.beginEvent(
      eventTagId,
      eventActionType,
      eventData
    )
    this.endEvent(beginEventData, undefined, false)
  }

  public userActionEvent = (
    eventTagId: number,
    eventActionType: string,
    { customData, eventFlags, payloadData }: EventData = {}
  ) =>
    this.event(eventTagId, eventActionType, {
      customData,
      eventFlags: {
        dataCategories: EnumObjects.DataCategories.ProductServiceUsage,
        ...eventFlags,
      },
      payloadData,
    })

  // returns event object that encapsulates the beginning time of the event. No data is written to sink
  public beginEvent = (
    eventTagId: number,
    eventActionType: string,
    eventData: EventData = {}
  ): IBeginEventData => {
    this.assert(eventTagId !== 0, 'Invalid eventTagId')
    this.debug(eventActionType, 'Invalid eventActionType')

    // log to sinks
    const sinkContexts: { [key: string]: ISinkContext } = {}
    this.safeEnumerateSinks(logSink => {
      const contextData = logSink.beginEvent(
        eventActionType,
        mergeWithDefaultEventFlags(eventData.eventFlags)
      )

      if (contextData) {
        sinkContexts[logSink.name()] = contextData
      }
    })

    return {
      eventActionType,
      eventData,
      eventEnded: false,
      eventStartTime: Date.now(),
      eventTagId,
      sinkContexts,
    }
  }

  // it's beginEvent, but defaults to the ProductServiceUsage data category
  public beginUserActionEvent = (
    eventTagId: number,
    eventActionType: string,
    { customData, eventFlags, payloadData }: EventData = {}
  ) =>
    this.beginEvent(eventTagId, eventActionType, {
      customData,
      eventFlags: {
        dataCategories: EnumObjects.DataCategories.ProductServiceUsage,
        ...eventFlags,
      },
      payloadData,
    })

  // writes event object to sinks by first calculating the time since the beginEvent
  public endEvent = (
    beginEventData: IBeginEventData,
    payloadData?: IEventPayloadData,
    includeResponseTime = true
  ) => {
    this.debug(beginEventData, 'Invalid beginEventData')
    this.assert(
      !beginEventData.eventEnded,
      `Event already ended - ${beginEventData.eventActionType}`
    )
    this.debug(beginEventData.eventData, 'Invalid eventData', beginEventData)
    this.debug(
      beginEventData.eventStartTime,
      'Invalid eventStartTime',
      beginEventData
    )
    this.debug(
      beginEventData.eventActionType,
      'Invalid eventActionType',
      beginEventData
    )

    if (!beginEventData || beginEventData.eventEnded) {
      return
    }

    if (includeResponseTime) {
      // calculate the overall time taken since the BeginEvent
      const { eventData, eventStartTime } = beginEventData
      const currentTime = Date.now()
      eventData.payloadData = {
        responseTime: currentTime - eventStartTime,
        ...eventData.payloadData,
      }
    }

    if (this.configuration && this.configuration.CustomMetadata) {
      beginEventData.eventData.customData = {
        ...this.configuration.CustomMetadata,
        ...beginEventData.eventData.customData,
      }
    }

    beginEventData.eventData.payloadData = {
      ...beginEventData.eventData.payloadData,
      ...payloadData,
    }

    // finally log the event
    this.sendEvent(beginEventData)
  }

  // creates a event type logEntry
  public createEventLogEntry = ({
    eventTagId,
    eventActionType,
    eventData,
  }: IBeginEventData): ILogEntry => {
    const { customData, eventFlags, payloadData } = eventData

    return {
      customData,
      eventActionData: {
        eventActionType,
        eventTagId,
        ...payloadData,
      },
      eventFlags: mergeWithDefaultEventFlags(eventFlags),
      metadata: getMetaData(this.configuration),
    }
  }

  // creates a message type logEntry
  public createMessageLogEntry = (messageData: MessageData): ILogEntry => {
    const { eventFlags, ..._messageData } = messageData

    return {
      eventFlags: mergeWithDefaultEventFlags(eventFlags),
      messageData: _messageData,
      metadata: getMetaData(this.configuration),
    }
  }

  // writes a given logentry to all sinks
  private readonly writeEntry = (logEntry: ILogEntry, localOnly = false) => {
    // ignore logs that are below log threshold
    if (logEntry.messageData && logEntry.messageData.logLevel < this.logLevel) {
      return
    }

    // log to sinks
    this.safeEnumerateSinks(logSink => {
      logSink.writeEntry(logEntry)
    }, localOnly)
  }

  // telemetry/instrumentation data event
  private readonly sendEvent = (beginEventData: IBeginEventData) => {
    const logEntry = this.createEventLogEntry(beginEventData)

    const sinkContexts = beginEventData.sinkContexts
    this.safeEnumerateSinks(logSink => {
      logSink.endEvent(logEntry, sinkContexts[logSink.name()])
    })
    beginEventData.eventEnded = true
  }

  private readonly safeEnumerateSinks = (
    callback: (logSink: ILogSink) => void,
    localOnly = false
  ) => {
    debug(this.logSinks, 'Invalid input', callback, this.logSinks)
    if (!this.logSinks) {
      return
    }

    for (const logSink of this.logSinks) {
      try {
        if (!localOnly || (logSink.isLocal && logSink.isLocal())) {
          callback(logSink)
        }
      } catch (error) {
        debug(false, 'Error with callback', error, logSink, callback)
      }
    }
  }
}
